import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SyncIcon from "@mui/icons-material/Sync";
import VerifiedIcon from "@mui/icons-material/Verified";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import GuaranteeChequeCustodyApi, {
  guaranteeChequeCustodyOperatorApi,
} from "../../../../../infrastructure/api/guaranteeChequeCustody";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";

const pick = (row, camel, pascal) => row?.[camel] ?? row?.[pascal];

const STATUS = {
  ReceivedAtOffice: 1,
  ReadyForBankDispatch: 2,
  DispatchedToBank: 3,
  ConfirmedAtBank: 4,
  SentToLendtech: 7,
  Suspended: 8,
};

const STATUS_OPTIONS = [
  { value: "", label: "همه وضعیت‌ها" },
  { value: "1", label: "وصول‌شده در دفتر" },
  { value: "2", label: "آماده ارسال به بانک" },
  { value: "3", label: "ارسال‌شده به بانک" },
  { value: "4", label: "تحویل تأییدشده در بانک" },
  { value: "7", label: "ارسال‌شده به لندتک" },
  { value: "8", label: "معلق" },
];

const formatMoney = (v) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : `${Number(v).toLocaleString("fa-IR")} ریال`;

const formatDt = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString("fa-IR");
  } catch {
    return "—";
  }
};

const statusColor = (status) => {
  switch (Number(status)) {
    case 1:
      return "info";
    case 2:
      return "warning";
    case 3:
      return "secondary";
    case 4:
      return "success";
    case 7:
      return "primary";
    case 8:
      return "error";
    default:
      return "default";
  }
};

const emptyReceiveForm = () => ({
  sourceType: 0,
  sayadiId: "",
  chequeSerial: "",
  bankName: "",
  accountNumber: "",
  amountIrr: "",
  caseTitle: "",
  bringerName: "",
  bringerMobile: "",
  bringerNationalCode: "",
  officeReceiptNotes: "",
});

const GuaranteeChequeCustodyPage = ({ source = "Management" }) => {
  const api =
    source === "SolarentOperator"
      ? guaranteeChequeCustodyOperatorApi
      : GuaranteeChequeCustodyApi;

  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [tick, setTick] = useState(0);

  const [receiveOpen, setReceiveOpen] = useState(false);
  const [receiveForm, setReceiveForm] = useState(emptyReceiveForm());

  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [dispatchForm, setDispatchForm] = useState({
    bankRecipientName: "",
    bankRecipientRole: "",
    bankRecipientMobile: "",
    targetBankName: "",
    dispatchTrackingCode: "",
    dispatchNotes: "",
  });

  const [bankConfirmOpen, setBankConfirmOpen] = useState(false);
  const [bankConfirmForm, setBankConfirmForm] = useState({
    bankConfirmedByName: "",
    notes: "",
  });

  const [lendtechOpen, setLendtechOpen] = useState(false);
  const [lendtechForm, setLendtechForm] = useState({
    lendtechPartnerName: "",
    lendtechRecipientName: "",
    lendtechTrackingCode: "",
    lendtechNotes: "",
  });

  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");

  const loadList = useCallback(async () => {
    const params = { page: 1, pageSize: 100 };
    if (status !== "") params.status = Number(status);
    if (search.trim()) params.search = search.trim();
    try {
      setLoading(true);
      const res = await api.getPagedList(params);
      if (res?.isSuccess && res.data) {
        setRows(res.data.items || res.data.Items || []);
        setTotal(res.data.total ?? res.data.Total ?? 0);
        setStats(res.data.stats || res.data.Stats || null);
      } else {
        toast.error(res?.message || "خطا در دریافت لیست");
      }
    } catch {
      toast.error("خطا در ارتباط با سرور");
    } finally {
      setLoading(false);
    }
  }, [api, search, status]);

  useEffect(() => {
    loadList();
  }, [loadList, tick]);

  const openDetail = async (id) => {
    try {
      const res = await api.getDetail(id);
      if (!res?.isSuccess) {
        toast.error(res?.message || "خطا در جزئیات");
        return;
      }
      setDetail(res.data);
      setDetailOpen(true);
    } catch {
      toast.error("خطا در دریافت جزئیات");
    }
  };

  const submitReceive = async () => {
    if (!receiveForm.sayadiId.trim() || !receiveForm.chequeSerial.trim()) {
      toast.error("شناسه صیادی و سریال الزامی است");
      return;
    }
    if (!receiveForm.bringerName.trim()) {
      toast.error("نام آورنده الزامی است");
      return;
    }
    try {
      const res = await api.registerOfficeReceipt({
        sourceType: Number(receiveForm.sourceType),
        sayadiId: receiveForm.sayadiId,
        chequeSerial: receiveForm.chequeSerial,
        bankName: receiveForm.bankName || null,
        accountNumber: receiveForm.accountNumber || null,
        amountIrr: receiveForm.amountIrr
          ? Number(String(receiveForm.amountIrr).replace(/,/g, ""))
          : null,
        caseTitle: receiveForm.caseTitle || null,
        bringerName: receiveForm.bringerName,
        bringerMobile: receiveForm.bringerMobile || null,
        bringerNationalCode: receiveForm.bringerNationalCode || null,
        officeReceiptNotes: receiveForm.officeReceiptNotes || null,
      });
      if (res?.isSuccess === false) {
        toast.error(res?.message || "خطا در ثبت وصول");
        return;
      }
      toast.success(res?.message || "وصول ثبت شد");
      setReceiveOpen(false);
      setReceiveForm(emptyReceiveForm());
      setTick((x) => x + 1);
    } catch {
      toast.error("خطا در ثبت وصول");
    }
  };

  const markReady = async (id) => {
    try {
      const res = await api.markReadyForBank({ custodyId: id });
      if (res?.isSuccess === false) {
        toast.error(res?.message || "خطا");
        return;
      }
      toast.success("آماده ارسال به بانک شد");
      setDetailOpen(false);
      setTick((x) => x + 1);
    } catch {
      toast.error("خطا در ثبت آماده‌سازی");
    }
  };

  const submitDispatch = async () => {
    const id = pick(detail, "id", "Id");
    if (!id || !dispatchForm.bankRecipientName.trim()) {
      toast.error("نام گیرنده بانک الزامی است");
      return;
    }
    try {
      const res = await api.dispatchToBank({
        custodyId: id,
        ...dispatchForm,
      });
      if (res?.isSuccess === false) {
        toast.error(res?.message || "خطا");
        return;
      }
      toast.success("ارسال به بانک ثبت شد");
      setDispatchOpen(false);
      setDetailOpen(false);
      setTick((x) => x + 1);
    } catch {
      toast.error("خطا در ثبت ارسال");
    }
  };

  const submitBankConfirm = async () => {
    const id = pick(detail, "id", "Id");
    if (!id || !bankConfirmForm.bankConfirmedByName.trim()) {
      toast.error("نام تأییدکننده بانک الزامی است");
      return;
    }
    try {
      const res = await api.confirmBankReceipt({
        custodyId: id,
        bankConfirmedByName: bankConfirmForm.bankConfirmedByName,
        notes: bankConfirmForm.notes || null,
      });
      if (res?.isSuccess === false) {
        toast.error(res?.message || "خطا");
        return;
      }
      toast.success("تحویل بانک تأیید شد");
      setBankConfirmOpen(false);
      setDetailOpen(false);
      setTick((x) => x + 1);
    } catch {
      toast.error("خطا در تأیید بانک");
    }
  };

  const submitLendtech = async () => {
    const id = pick(detail, "id", "Id");
    if (
      !id ||
      !lendtechForm.lendtechPartnerName.trim() ||
      !lendtechForm.lendtechRecipientName.trim()
    ) {
      toast.error("نام لندتک و گیرنده الزامی است");
      return;
    }
    try {
      const res = await api.dispatchToLendtech({
        custodyId: id,
        ...lendtechForm,
      });
      if (res?.isSuccess === false) {
        toast.error(res?.message || "خطا");
        return;
      }
      toast.success("ارسال به لندتک ثبت شد");
      setLendtechOpen(false);
      setDetailOpen(false);
      setTick((x) => x + 1);
    } catch {
      toast.error("خطا در ثبت ارسال به لندتک");
    }
  };

  const submitSuspend = async () => {
    const id = pick(detail, "id", "Id");
    if (!id || !suspendReason.trim()) {
      toast.error("دلیل تعلیق الزامی است");
      return;
    }
    try {
      const res = await api.suspend({
        custodyId: id,
        suspensionReason: suspendReason.trim(),
      });
      if (res?.isSuccess === false) {
        toast.error(res?.message || "خطا");
        return;
      }
      toast.success("چک معلق شد");
      setSuspendOpen(false);
      setDetailOpen(false);
      setTick((x) => x + 1);
    } catch {
      toast.error("خطا در تعلیق");
    }
  };

  const resumeSuspend = async (id) => {
    try {
      const res = await api.resumeFromSuspend({ custodyId: id });
      if (res?.isSuccess === false) {
        toast.error(res?.message || "خطا");
        return;
      }
      toast.success("از تعلیق خارج شد");
      setDetailOpen(false);
      setTick((x) => x + 1);
    } catch {
      toast.error("خطا در خروج از تعلیق");
    }
  };

  const statChips = useMemo(() => {
    if (!stats) return null;
    return (
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Chip
          icon={<ReceiptLongIcon />}
          label={`وصول دفتر: ${pick(stats, "receivedAtOffice", "ReceivedAtOffice") ?? 0}`}
          color="info"
          variant="outlined"
        />
        <Chip
          icon={<AccountBalanceIcon />}
          label={`آماده بانک: ${pick(stats, "readyForBankDispatch", "ReadyForBankDispatch") ?? 0}`}
          color="warning"
          variant="outlined"
        />
        <Chip
          icon={<LocalShippingIcon />}
          label={`ارسال‌شده: ${pick(stats, "dispatchedToBank", "DispatchedToBank") ?? 0}`}
          color="secondary"
          variant="outlined"
        />
        <Chip
          icon={<VerifiedIcon />}
          label={`تحویل بانک: ${pick(stats, "confirmedAtBank", "ConfirmedAtBank") ?? 0}`}
          color="success"
          variant="outlined"
        />
        <Chip
          color="primary"
          variant="outlined"
          label={`لندتک: ${pick(stats, "sentToLendtech", "SentToLendtech") ?? 0}`}
        />
        <Chip
          color="error"
          variant="outlined"
          label={`معلق: ${pick(stats, "suspended", "Suspended") ?? 0}`}
        />
        <Chip label={`کل: ${total}`} />
      </Stack>
    );
  }, [stats, total]);

  const detailStatus = Number(pick(detail, "status", "Status"));

  return (
    <Box sx={{ p: 2 }}>
      <BreadcrumbsComponent />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ sm: "center" }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5">دفتر چک‌های ضمانت</Typography>
          <Typography variant="body2" color="text.secondary">
            زنجیره: آورنده → تحویل‌گیرنده دفتر → ارسال‌کننده → گیرنده بانک — تا هیچ چکی گم نشود.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<SyncIcon />}
            onClick={() => setTick((x) => x + 1)}
            disabled={loading}
          >
            بروزرسانی
          </Button>
          <Button
            variant="contained"
            startIcon={<ReceiptLongIcon />}
            onClick={() => {
              setReceiveForm(emptyReceiveForm());
              setReceiveOpen(true);
            }}
          >
            ثبت وصول دفتر
          </Button>
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        ۱) وصول در دفتر · ۲) ارسال به بانک یا لندتک · ۳) در صورت نیاز معلق‌سازی با دلیل —
        همه نقش‌ها در تاریخچه ثبت می‌شوند.
      </Alert>

      {statChips}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              size="small"
              fullWidth
              label="جستجو (صیاد / سریال / آورنده / کد رهگیری)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setTick((x) => x + 1);
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>وضعیت</InputLabel>
              <Select
                label="وضعیت"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setTick((x) => x + 1);
                }}
              >
                {STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value || "all"} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>پرونده</TableCell>
              <TableCell>صیاد / سریال</TableCell>
              <TableCell>وضعیت</TableCell>
              <TableCell>آورنده</TableCell>
              <TableCell>تحویل‌گیرنده</TableCell>
              <TableCell>ارسال‌کننده</TableCell>
              <TableCell>گیرنده بانک</TableCell>
              <TableCell align="left">عملیات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(rows || []).map((row) => {
              const id = pick(row, "id", "Id");
              return (
                <TableRow key={id} hover>
                  <TableCell>{pick(row, "caseTitle", "CaseTitle") || "—"}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {pick(row, "sayadiId", "SayadiId") || "—"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {pick(row, "chequeSerial", "ChequeSerial") || ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={statusColor(pick(row, "status", "Status"))}
                      label={pick(row, "statusTitle", "StatusTitle") || "—"}
                    />
                  </TableCell>
                  <TableCell>{pick(row, "bringerName", "BringerName") || "—"}</TableCell>
                  <TableCell>
                    {pick(row, "officeReceiverNameSnapshot", "OfficeReceiverNameSnapshot") ||
                      "—"}
                  </TableCell>
                  <TableCell>
                    {pick(row, "dispatchSenderNameSnapshot", "DispatchSenderNameSnapshot") ||
                      "—"}
                  </TableCell>
                  <TableCell>
                    {pick(row, "bankRecipientName", "BankRecipientName") || "—"}
                  </TableCell>
                  <TableCell align="left">
                    <Button size="small" onClick={() => openDetail(id)}>
                      پیگیری
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {!rows?.length && !loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  موردی ثبت نشده است
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ثبت وصول */}
      <Dialog open={receiveOpen} onClose={() => setReceiveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>ثبت وصول چک در دفتر</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="عنوان پرونده"
              fullWidth
              size="small"
              value={receiveForm.caseTitle}
              onChange={(e) =>
                setReceiveForm((f) => ({ ...f, caseTitle: e.target.value }))
              }
            />
            <TextField
              label="شناسه صیادی *"
              fullWidth
              size="small"
              value={receiveForm.sayadiId}
              onChange={(e) =>
                setReceiveForm((f) => ({ ...f, sayadiId: e.target.value }))
              }
            />
            <TextField
              label="سریال چک *"
              fullWidth
              size="small"
              value={receiveForm.chequeSerial}
              onChange={(e) =>
                setReceiveForm((f) => ({ ...f, chequeSerial: e.target.value }))
              }
            />
            <TextField
              label="بانک / مبلغ (ریال)"
              fullWidth
              size="small"
              value={receiveForm.bankName}
              onChange={(e) =>
                setReceiveForm((f) => ({ ...f, bankName: e.target.value }))
              }
              helperText="نام بانک"
            />
            <TextField
              label="مبلغ (ریال)"
              fullWidth
              size="small"
              value={receiveForm.amountIrr}
              onChange={(e) =>
                setReceiveForm((f) => ({ ...f, amountIrr: e.target.value }))
              }
            />
            <Alert severity="warning">نقش‌های الزامی این مرحله</Alert>
            <TextField
              label="آورنده *"
              fullWidth
              size="small"
              value={receiveForm.bringerName}
              onChange={(e) =>
                setReceiveForm((f) => ({ ...f, bringerName: e.target.value }))
              }
            />
            <TextField
              label="موبایل آورنده"
              fullWidth
              size="small"
              value={receiveForm.bringerMobile}
              onChange={(e) =>
                setReceiveForm((f) => ({ ...f, bringerMobile: e.target.value }))
              }
            />
            <TextField
              label="کد ملی آورنده"
              fullWidth
              size="small"
              value={receiveForm.bringerNationalCode}
              onChange={(e) =>
                setReceiveForm((f) => ({
                  ...f,
                  bringerNationalCode: e.target.value,
                }))
              }
            />
            <TextField
              label="توضیحات وصول"
              fullWidth
              multiline
              minRows={2}
              value={receiveForm.officeReceiptNotes}
              onChange={(e) =>
                setReceiveForm((f) => ({
                  ...f,
                  officeReceiptNotes: e.target.value,
                }))
              }
            />
            <Typography variant="caption" color="text.secondary">
              تحویل‌گیرنده دفتر: کاربر فعلی سیستم (به‌صورت خودکار ثبت می‌شود).
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiveOpen(false)}>انصراف</Button>
          <Button variant="contained" onClick={submitReceive}>
            ثبت وصول
          </Button>
        </DialogActions>
      </Dialog>

      {/* جزئیات / اقدامات */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>پیگیری چک ضمانت</DialogTitle>
        <DialogContent dividers>
          {detail ? (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  color={statusColor(detailStatus)}
                  label={pick(detail, "statusTitle", "StatusTitle")}
                />
                <Typography variant="body2" color="text.secondary">
                  {pick(detail, "caseTitle", "CaseTitle") || "—"}
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    صیاد
                  </Typography>
                  <Typography>{pick(detail, "sayadiId", "SayadiId") || "—"}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    سریال
                  </Typography>
                  <Typography>
                    {pick(detail, "chequeSerial", "ChequeSerial") || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    مبلغ
                  </Typography>
                  <Typography>
                    {formatMoney(pick(detail, "amountIrr", "AmountIrr"))}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    بانک
                  </Typography>
                  <Typography>{pick(detail, "bankName", "BankName") || "—"}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    آورنده
                  </Typography>
                  <Typography fontWeight={600}>
                    {pick(detail, "bringerName", "BringerName") || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    تحویل‌گیرنده دفتر
                  </Typography>
                  <Typography fontWeight={600}>
                    {pick(detail, "officeReceiverNameSnapshot", "OfficeReceiverNameSnapshot") ||
                      "—"}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    ارسال‌کننده
                  </Typography>
                  <Typography fontWeight={600}>
                    {pick(detail, "dispatchSenderNameSnapshot", "DispatchSenderNameSnapshot") ||
                      "—"}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    گیرنده بانک
                  </Typography>
                  <Typography fontWeight={600}>
                    {pick(detail, "bankRecipientName", "BankRecipientName") || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    لندتک / گیرنده
                  </Typography>
                  <Typography fontWeight={600}>
                    {pick(detail, "lendtechPartnerName", "LendtechPartnerName") || "—"}
                    {pick(detail, "lendtechRecipientName", "LendtechRecipientName")
                      ? ` / ${pick(detail, "lendtechRecipientName", "LendtechRecipientName")}`
                      : ""}
                  </Typography>
                </Grid>
                {detailStatus === STATUS.Suspended ? (
                  <Grid item xs={12}>
                    <Alert severity="error">
                      معلق: {pick(detail, "suspensionReason", "SuspensionReason") || "—"}
                      {pick(detail, "suspendedByNameSnapshot", "SuspendedByNameSnapshot")
                        ? ` — توسط ${pick(detail, "suspendedByNameSnapshot", "SuspendedByNameSnapshot")}`
                        : ""}
                    </Alert>
                  </Grid>
                ) : null}
              </Grid>

              <Typography variant="subtitle2">تاریخچه زنجیره</Typography>
              <Stack spacing={1}>
                {(pick(detail, "events", "Events") || []).map((ev) => (
                  <Paper key={pick(ev, "id", "Id")} variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatDt(pick(ev, "occurredAt", "OccurredAt"))} ·{" "}
                      {pick(ev, "eventTypeTitle", "EventTypeTitle")}
                    </Typography>
                    <Typography variant="body2">
                      {pick(ev, "actorRoleTitle", "ActorRoleTitle")}:{" "}
                      {pick(ev, "actorNameSnapshot", "ActorNameSnapshot") || "—"}
                      {pick(ev, "counterpartyName", "CounterpartyName")
                        ? ` ← ${pick(ev, "counterpartyRoleTitle", "CounterpartyRoleTitle") || "طرف"}: ${pick(ev, "counterpartyName", "CounterpartyName")}`
                        : ""}
                    </Typography>
                    {pick(ev, "notes", "Notes") ? (
                      <Typography variant="caption">{pick(ev, "notes", "Notes")}</Typography>
                    ) : null}
                  </Paper>
                ))}
              </Stack>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
          <Button onClick={() => setDetailOpen(false)}>بستن</Button>
          {detailStatus === STATUS.ReceivedAtOffice ? (
            <Button variant="outlined" onClick={() => markReady(pick(detail, "id", "Id"))}>
              پرونده نهایی شد — آماده بانک
            </Button>
          ) : null}
          {detailStatus === STATUS.ReceivedAtOffice ||
          detailStatus === STATUS.ReadyForBankDispatch ? (
            <Button
              variant="contained"
              startIcon={<LocalShippingIcon />}
              onClick={() => {
                setDispatchForm({
                  bankRecipientName: "",
                  bankRecipientRole: "",
                  bankRecipientMobile: "",
                  targetBankName: pick(detail, "bankName", "BankName") || "",
                  dispatchTrackingCode: "",
                  dispatchNotes: "",
                });
                setDispatchOpen(true);
              }}
            >
              ثبت ارسال به بانک
            </Button>
          ) : null}
          {detailStatus === STATUS.DispatchedToBank ? (
            <Button
              variant="contained"
              color="success"
              startIcon={<VerifiedIcon />}
              onClick={() => {
                setBankConfirmForm({ bankConfirmedByName: "", notes: "" });
                setBankConfirmOpen(true);
              }}
            >
              تأیید تحویل بانک
            </Button>
          ) : null}
          {detailStatus === STATUS.ReceivedAtOffice ||
          detailStatus === STATUS.ReadyForBankDispatch ||
          detailStatus === STATUS.SentToLendtech ? (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                setLendtechForm({
                  lendtechPartnerName: "",
                  lendtechRecipientName: "",
                  lendtechTrackingCode: "",
                  lendtechNotes: "",
                });
                setLendtechOpen(true);
              }}
            >
              ثبت ارسال به لندتک
            </Button>
          ) : null}
          {detailStatus !== STATUS.Suspended &&
          detailStatus !== STATUS.ConfirmedAtBank ? (
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setSuspendReason("");
                setSuspendOpen(true);
              }}
            >
              معلق کردن
            </Button>
          ) : null}
          {detailStatus === STATUS.Suspended ? (
            <Button
              variant="contained"
              color="warning"
              onClick={() => resumeSuspend(pick(detail, "id", "Id"))}
            >
              خروج از تعلیق
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog open={dispatchOpen} onClose={() => setDispatchOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>ثبت ارسال چک به بانک</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              ارسال‌کننده: کاربر فعلی سیستم
            </Typography>
            <TextField
              label="گیرنده در بانک *"
              fullWidth
              size="small"
              value={dispatchForm.bankRecipientName}
              onChange={(e) =>
                setDispatchForm((f) => ({ ...f, bankRecipientName: e.target.value }))
              }
            />
            <TextField
              label="سمت گیرنده"
              fullWidth
              size="small"
              value={dispatchForm.bankRecipientRole}
              onChange={(e) =>
                setDispatchForm((f) => ({ ...f, bankRecipientRole: e.target.value }))
              }
            />
            <TextField
              label="موبایل گیرنده"
              fullWidth
              size="small"
              value={dispatchForm.bankRecipientMobile}
              onChange={(e) =>
                setDispatchForm((f) => ({ ...f, bankRecipientMobile: e.target.value }))
              }
            />
            <TextField
              label="بانک مقصد"
              fullWidth
              size="small"
              value={dispatchForm.targetBankName}
              onChange={(e) =>
                setDispatchForm((f) => ({ ...f, targetBankName: e.target.value }))
              }
            />
            <TextField
              label="کد رهگیری / رسید"
              fullWidth
              size="small"
              value={dispatchForm.dispatchTrackingCode}
              onChange={(e) =>
                setDispatchForm((f) => ({
                  ...f,
                  dispatchTrackingCode: e.target.value,
                }))
              }
            />
            <TextField
              label="توضیحات ارسال"
              fullWidth
              multiline
              minRows={2}
              value={dispatchForm.dispatchNotes}
              onChange={(e) =>
                setDispatchForm((f) => ({ ...f, dispatchNotes: e.target.value }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDispatchOpen(false)}>انصراف</Button>
          <Button variant="contained" onClick={submitDispatch}>
            ثبت ارسال
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bankConfirmOpen}
        onClose={() => setBankConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>تأیید تحویل در بانک</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="نام تأییدکننده / گیرنده نهایی بانک *"
              fullWidth
              size="small"
              value={bankConfirmForm.bankConfirmedByName}
              onChange={(e) =>
                setBankConfirmForm((f) => ({
                  ...f,
                  bankConfirmedByName: e.target.value,
                }))
              }
            />
            <TextField
              label="توضیحات"
              fullWidth
              multiline
              minRows={2}
              value={bankConfirmForm.notes}
              onChange={(e) =>
                setBankConfirmForm((f) => ({ ...f, notes: e.target.value }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBankConfirmOpen(false)}>انصراف</Button>
          <Button variant="contained" color="success" onClick={submitBankConfirm}>
            تأیید
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={lendtechOpen} onClose={() => setLendtechOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>ثبت ارسال چک به لندتک</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              ارسال‌کننده: کاربر فعلی سیستم
            </Typography>
            <TextField
              label="نام لندتک / شریک *"
              fullWidth
              size="small"
              value={lendtechForm.lendtechPartnerName}
              onChange={(e) =>
                setLendtechForm((f) => ({ ...f, lendtechPartnerName: e.target.value }))
              }
              placeholder="مثلاً مهرسان / برق"
            />
            <TextField
              label="گیرنده در لندتک *"
              fullWidth
              size="small"
              value={lendtechForm.lendtechRecipientName}
              onChange={(e) =>
                setLendtechForm((f) => ({
                  ...f,
                  lendtechRecipientName: e.target.value,
                }))
              }
            />
            <TextField
              label="کد رهگیری / رسید"
              fullWidth
              size="small"
              value={lendtechForm.lendtechTrackingCode}
              onChange={(e) =>
                setLendtechForm((f) => ({
                  ...f,
                  lendtechTrackingCode: e.target.value,
                }))
              }
            />
            <TextField
              label="توضیحات"
              fullWidth
              multiline
              minRows={2}
              value={lendtechForm.lendtechNotes}
              onChange={(e) =>
                setLendtechForm((f) => ({ ...f, lendtechNotes: e.target.value }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLendtechOpen(false)}>انصراف</Button>
          <Button variant="contained" onClick={submitLendtech}>
            ثبت ارسال به لندتک
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={suspendOpen} onClose={() => setSuspendOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>معلق کردن چک</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              چک معلق از صف فعال خارج می‌شود ولی در فیلتر «معلق» قابل پیگیری است.
            </Alert>
            <TextField
              label="دلیل تعلیق *"
              fullWidth
              multiline
              minRows={3}
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuspendOpen(false)}>انصراف</Button>
          <Button variant="contained" color="error" onClick={submitSuspend}>
            معلق کن
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GuaranteeChequeCustodyPage;
